import { useState } from 'react';
import { Sparkles, Check, X } from 'lucide-react';
import { useSuggestMealSmart, useSuggestMealSimple } from '../lib/queries';
import { ApiError, limitReason, type LimitReason, type MealType, type MealSuggestItem } from '../lib/api';
import { LimitReachedModal } from './LimitReachedModal';

interface Props {
  patientId: string | null;
  mealType: MealType;
  /** Contesto libero (kcal target, allergie, preferenze) inviato al modello. */
  context?: string;
  /** Callback con il pasto accettato (name + items). */
  onAccept: (suggestion: { name: string; items: MealSuggestItem[] }) => void;
  /** Disabilita bottone (es. slot già pieno o fasting). */
  disabled?: boolean;
  label?: string;
}

interface SuggestState {
  loading: boolean;
  result: { name: string; items: MealSuggestItem[]; reasoning?: string | null } | null;
  error: string | null;
}

/**
 * Sprint 9.B — Bottone AI suggest riusabile.
 * Usa /api/pro/meal/suggest-smart (Anthropic Opus 4.7 via OpenRouter) se patient_id presente
 * → include patient_context (goal/allergie/preferenze/adherence).
 * Fallback su /api/pro/meal/suggest (Mistral Small) se patient_id mancante.
 *
 * Mostra modale inline con: loading spinner, errore, oppure result + Accetta/Annulla.
 */
export function AISuggestButton({ patientId, mealType, context, onAccept, disabled, label }: Props) {
  const [state, setState] = useState<SuggestState>({ loading: false, result: null, error: null });
  const [limit, setLimit] = useState<LimitReason | null>(null);
  const smartMutation = useSuggestMealSmart();
  const simpleMutation = useSuggestMealSimple();

  const open = () => {
    setState({ loading: true, result: null, error: null });
    void (async () => {
      try {
        if (patientId) {
          const res = await smartMutation.mutateAsync({
            patient_id: patientId,
            meal_type: mealType,
            context: context || undefined,
          });
          setState({ loading: false, result: res, error: null });
        } else {
          const res = await simpleMutation.mutateAsync({
            meal_type: mealType,
            context: context || undefined,
          });
          setState({ loading: false, result: { ...res, reasoning: null }, error: null });
        }
      } catch (err: unknown) {
        const e = err as ApiError | Error;
        // Limite piano (quota AI esaurita / smart eco-only) → modale upgrade, non errore grezzo.
        const reason = limitReason(err);
        if (reason) { setState({ loading: false, result: null, error: null }); setLimit(reason); return; }
        const msg = e instanceof ApiError && e.details ? `${e.message} — ${e.details}` : e.message || String(err);
        setState({ loading: false, result: null, error: msg });
      }
    })();
  };

  const accept = () => {
    if (state.result) {
      onAccept({ name: state.result.name, items: state.result.items });
      setState({ loading: false, result: null, error: null });
    }
  };

  const close = () => {
    setState({ loading: false, result: null, error: null });
  };

  const showModal = state.loading || state.result !== null || state.error !== null;

  return (
    <>
      <button
        type="button"
        onClick={open}
        disabled={disabled || state.loading}
        title={patientId ? 'Suggerimento AI contestualizzato per il paziente' : 'Suggerimento AI generico'}
        style={{
          padding: '6px 10px',
          background: disabled ? 'var(--line)' : 'linear-gradient(135deg, #6b46c1, #d946ef)',
          color: '#fff',
          border: 0,
          borderRadius: 6,
          fontWeight: 700,
          fontSize: 11,
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
          letterSpacing: 0.3,
        }}
      >
        <Sparkles size={12} /> {label || 'AI'}
      </button>

      {showModal && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={close}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--surface)',
              borderRadius: 14,
              padding: 22,
              maxWidth: 460,
              width: '100%',
              boxShadow: '0 24px 60px rgba(0,0,0,0.32)',
              maxHeight: '85vh',
              overflowY: 'auto',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
              <Sparkles size={18} color="#a855f7" />
              <h3 style={{ fontFamily: 'var(--display)', fontSize: 17, fontWeight: 700, margin: 0 }}>
                Suggerimento AI
              </h3>
            </div>

            {state.loading && (
              <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--mute)' }}>
                Sto pensando un pasto bilanciato…
              </div>
            )}

            {state.error && (
              <div
                style={{
                  background: 'var(--red-soft, #fff0f0)',
                  border: '1px solid var(--red)',
                  borderRadius: 8,
                  padding: 12,
                  fontSize: 13,
                  color: 'var(--red)',
                  marginBottom: 14,
                }}
              >
                {state.error}
              </div>
            )}

            {state.result && (
              <>
                <div
                  style={{
                    fontFamily: 'var(--display)',
                    fontSize: 18,
                    fontWeight: 700,
                    marginBottom: 10,
                    color: 'var(--ink)',
                  }}
                >
                  {state.result.name}
                </div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, lineHeight: 1.65 }}>
                  {state.result.items.map((it, i) => (
                    <li key={i}>
                      <b>{it.ingredient}</b>
                      {it.quantity && <> — {it.quantity} {it.unit ?? ''}</>}
                    </li>
                  ))}
                </ul>
                {state.result.reasoning && (
                  <div
                    style={{
                      marginTop: 14,
                      padding: 10,
                      background: 'var(--surface-2)',
                      borderRadius: 8,
                      fontSize: 12,
                      color: 'var(--ink-2)',
                      fontStyle: 'italic',
                      lineHeight: 1.5,
                    }}
                  >
                    {state.result.reasoning}
                  </div>
                )}
              </>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
              <button
                type="button"
                onClick={close}
                disabled={state.loading}
                style={{
                  border: '1px solid var(--line)',
                  background: 'var(--surface)',
                  color: 'var(--ink-2)',
                  padding: '8px 14px',
                  borderRadius: 8,
                  fontWeight: 600,
                  cursor: state.loading ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <X size={13} /> {state.error ? 'Chiudi' : 'Annulla'}
              </button>
              {state.result && (
                <button
                  type="button"
                  onClick={accept}
                  style={{
                    border: 0,
                    background: 'var(--green-deep)',
                    color: '#fff',
                    padding: '8px 14px',
                    borderRadius: 8,
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                  }}
                >
                  <Check size={13} /> Accetta
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {limit && <LimitReachedModal reason={limit} onClose={() => setLimit(null)} />}
    </>
  );
}
